/**
 * acceptance: the placement rule's pure parts. Where a geocode answer may
 * be placed, whether it is placed approximately, why an address that
 * holds no point was not placed, and how stored free text splits into
 * its five postal parts.
 *
 * THE RULE. A result is placed only when its state is the typed state,
 * or, with none typed, one of the tenant's working states. A structured
 * address (the five postal fields) with a street line is placed on the
 * state alone and never marked approximate. A structured address whose
 * line 1 is a PO Box line, or which has a town and no street, is a town
 * ask: only its town, state and ZIP are sent, it is placed only when
 * anchored (below) and it is always approximate. Free text (one text
 * column sent whole) is sent only when it has a street shape, is a ZIP
 * alone, or is a town and a state; a town and a state is sent whatever
 * the working states, since the anchor decides whether it is placed. A
 * ZIP typed alone stands for its own state: it is placed wherever that
 * ZIP is, and only when the answer carries it.
 *   - A town and a state (free text, or a structured town ask) is placed
 *     only on a town-level Geography answer (a Municipality or
 *     PostalCodeArea) or a street-level answer (Point Address, Address
 *     Range, Street, Cross Street) that is anchored in what was typed:
 *     one of the answer's town names ends the town position (a name
 *     field that lists several towns, comma-separated, as the answer for
 *     a ZIP covering several towns does, names each of them); or the
 *     answer carries the typed ZIP and either names no town or the town
 *     position names none (no letters left once box and unit words and
 *     every token holding a digit are set aside). A carried ZIP never
 *     overrides a town position that names a different town. Any other
 *     answer is not found. A placed town and state is always approximate,
 *     whatever the answer's type. Text whose town position ends in a
 *     county word is never town text and is not sent.
 *   - A typed ZIP the answer does not carry marks the pin approximate when
 *     a state was read, and refuses it (zip_mismatch) when none was.
 *   - A free-text street is approximate unless the segment it was found
 *     in opens with a digit house number or the answer carries the typed
 *     ZIP.
 *
 * WHAT WAS TYPED. A structured state is read by typedStateCode
 * (usStates.ts). Free text is read by readFreeTextAddress: a country is
 * dropped first, compared with its dots and spaces removed ("USA",
 * "U.S.A.", "U. S. A."), whether it is the final segment or follows the
 * ZIP at the end of the last segment with no comma; a final
 * segment that is only a ZIP is set aside and read as the last token of
 * the segment before it; the ZIP is the last token of the last segment,
 * and a state is read only from that segment. A code alone in its own
 * segment is read as it is. A code ending a segment of several words is
 * read only when it is not an address word ("Ct", "Co", "Ne") and not
 * "La" (Lane); in a single segment with no ZIP it must also be written
 * in capitals after a word with a letter. A full state name is read when
 * it is that whole segment and at least three segments were typed, or,
 * with two, when the name has a space in it (from the states table). The
 * street shape is judged after the ZIP and the state are taken out, so a
 * town, state and ZIP is not a street address; a street is a house number
 * then a word, a route, a street suffix ending the segment, or a house
 * number (digits after a leading name, or One to Ten spelled with a
 * capital) with a street suffix two or more words on.
 *
 * THE SPLIT (splitFreeTextAddress) reads stored text with the same
 * segmenter and state reader and gives its five parts back only when
 * every rule holds, in order; the first rule that fails names why the
 * text is kept:
 *   1. line breaks and ";" are commas, a token of only dots and commas is
 *      dropped, whitespace collapses; nothing left is blank;
 *   2. one address: a ZIP in a segment before the last, a state segment
 *      followed by a numbered or PO Box segment, or a segment split by
 *      " and ", " & " or "/" into words and a numbered street, is two
 *      addresses (more_than_one_address);
 *   3. the segments are the reader's (a country dropped, a final ZIP
 *      folded into the segment before it);
 *   4. a state must be read (no_state);
 *   5. the entries left once the state and ZIP are out: one is the town
 *      in directory mode and too few otherwise (one_part_only); four or
 *      more are too many (too_many_parts);
 *  5a. before rule 5, one entry holding the street, a unit and the town
 *      with no comma at all ("200 Example Ave Suite 124A Springfield")
 *      reads as three: a unit word and one number or letter ("Suite
 *      124A", "Apt B"), or a "#" number ("#5"), with the street's words
 *      before it making a street line (rule 6) and the town's words after
 *      it. Two entries are left as they are, so a street, then a unit and
 *      the town after a comma ("12 Main St, Apt B Newark") is still kept;
 *   6. the street line opens with a digit house number and a word, or is
 *      a PO Box line with nothing after its number (no_house_number);
 *   7. a middle entry is a unit line (second_line_not_a_unit);
 *   8. the town holds a letter and no digit, ends in no county word and
 *      holds no unit, floor, building, level, rear or ordinal word
 *      (town_not_a_town);
 *   9. line 1 and line 2 fit 200 characters and the town 100 (over_cap);
 *  10. the parts hold every word of the text, in order (words_left_over);
 *  12. in home mode a ZIP was read (no_zip).
 * Rule 11, confirmation by the geocoder, is the split script's
 * (tools/recon/split-stored-addresses.ts).
 *
 * The readers of an answer (parseGeocodeMatch, listedTownNames,
 * matchZips, canonicalRouteName) and the address, unit and PO Box tables
 * are the address check's (geocodeMatch.ts, exported by addressCheck.ts);
 * nothing here copies them.
 */

import { US_STATE_CODES, US_STATES, US_TERRITORIES, normalizeZip, typedStateCode } from '../usStates';
import {
  DIRECTIONALS,
  FLOOR_KEYWORDS,
  ROUTE_LEADS,
  ROUTE_QUALIFIERS,
  STREET_RESULT_TYPES,
  STREET_SUFFIXES,
  STREET_WORDS,
  TOWN_TOKENS,
  UNIT_KEYWORDS,
  canonicalRouteName,
  isOrdinal,
  isPoBoxLine,
  isUnitSegment,
  listedTownNames,
  poBoxTail,
  type GeocodeMatch,
} from './geocodeMatch';
import type { GeocodeAddress, GeocodeAsk, GeocodeRejection, GeocodeRule } from './azureMaps';

/** What free text says about itself, read before any request. */
export interface FreeTextAddress {
  /** The first five digits of a trailing ZIP, else null. */
  zip: string | null;
  /** The typed state's code, else null. */
  state: string | null;
  /** The whole text is one ZIP. */
  zipOnly: boolean;
  /** A house number and a street, a route, or a street suffix. */
  streetShape: boolean;
  /** Some segment is a street that opens with a digit house number. */
  streetOpensWithNumber: boolean;
  /** No street shape, a state read, and a town before it that is not a
   *  county. */
  townShape: boolean;
  /** The segments the street shape is judged on, the ZIP and the state
   *  taken out. The last is the town position. */
  judged: string[];
}

/** The placement verdict: why the answer is refused, else null, and
 *  whether a placed answer is approximate. */
export interface PlacementVerdict {
  rejected: GeocodeRejection | null;
  approximate: boolean;
}

/** Why a geocoded row holds no point, derived from its own text. */
export type NotPlacedReason = 'no_street_address' | 'not_found_add_state' | 'not_found';

const COUNTRY_SEGMENTS: ReadonlySet<string> = new Set(['usa', 'us', 'united states', 'united states of america']);
const ZIP_TOKEN = /^\d{5}(-\d{4})?$/;
const HOUSE_NUMBER_TOKEN = /^\d+[A-Za-z]?(-\d+[A-Za-z]?)?$/;

/** A country as it is compared: lower case, dots and spaces removed. */
function countryKey(text: string): string {
  return text.replace(/[.\s]/g, '').toLowerCase();
}

/** COUNTRY_SEGMENTS as they are compared. */
const COUNTRY_KEYS: ReadonlySet<string> = new Set([...COUNTRY_SEGMENTS].map(countryKey));

/** A house number spelled out, read only with a capital first letter. */
const SPELLED_HOUSE_NUMBERS: ReadonlySet<string> = new Set(['one', 'two', 'three', 'four', 'five', 'six', 'seven', 'eight', 'nine', 'ten']);

/** A trailing word that is a street word and also a state code: never
 *  read as the state from a segment of several words. */
const NOT_A_STATE_WORDS: ReadonlySet<string> = new Set(['la']);

/** A last word that makes a town position a county, not a town. */
const COUNTY_WORDS: ReadonlySet<string> = new Set(['county', 'co', 'cnty']);

/** The Azure answer a town and a state may be placed on: a town-level
 *  Geography, or any of STREET_RESULT_TYPES. */
const TOWN_RESULT_TYPE = 'Geography';
const TOWN_ENTITY_TYPES: ReadonlySet<string> = new Set(['Municipality', 'PostalCodeArea']);

/** A word of a box or a unit, set aside with every token holding a digit
 *  when asking whether a town position names a town. Dots removed. */
const BOX_AND_UNIT_WORDS: ReadonlySet<string> = new Set([
  'po',
  'box',
  'suite',
  'ste',
  'unit',
  'apt',
  'floor',
  'fl',
  'building',
  'bldg',
  'room',
  'rm',
]);

/** A town name's municipal suffix, after TOWN_TOKENS; "city" is never one. */
const MUNICIPAL_SUFFIXES: ReadonlySet<string> = new Set(['twp', 'boro', 'village', 'town']);

/** A lowercase word that is a key or value of an address table. */
const ADDRESS_WORDS: ReadonlySet<string> = new Set(
  [
    ...STREET_SUFFIXES.keys(),
    ...STREET_SUFFIXES.values(),
    ...STREET_WORDS.keys(),
    ...STREET_WORDS.values(),
    ...DIRECTIONALS.keys(),
    ...DIRECTIONALS.values(),
    ...ROUTE_QUALIFIERS,
  ].map((w) => w.toLowerCase()),
);

/** A street suffix spelling or its canonical form. */
const SUFFIX_WORDS: ReadonlySet<string> = new Set([...STREET_SUFFIXES.keys(), ...STREET_SUFFIXES.values()]);

/** The first word of every route opening that is not a state code. */
const ROUTE_OPENING_WORDS: ReadonlySet<string> = new Set([
  ...[...ROUTE_QUALIFIERS].map((q) => q.split(' ')[0]!),
  ...ROUTE_LEADS,
]);

/** A full state or territory name, dots removed, uppercase, to its code. */
const CODE_BY_NAME: ReadonlyMap<string, string> = new Map(
  [...US_STATES, ...US_TERRITORIES].map((s) => [normalName(s.name), s.code]),
);

/** The names of CODE_BY_NAME that contain a space: the only names read
 *  from the second of two segments. */
const CODE_BY_SPACED_NAME: ReadonlyMap<string, string> = new Map([...CODE_BY_NAME].filter(([name]) => name.includes(' ')));

function normalName(text: string): string {
  return text.replace(/\./g, '').replace(/\s+/g, ' ').trim().toUpperCase();
}

/** A token read as a state code, any case, dots already removed. */
function codeOf(token: string): string | null {
  const upper = token.toUpperCase();
  return US_STATE_CODES.has(upper) ? upper : null;
}

function isAddressWord(token: string): boolean {
  return ADDRESS_WORDS.has(token.toLowerCase());
}

/** The one guard on a code that ends a segment of several words: not an
 *  address word, and not a street word that is also a code. */
function passesCodeGuard(token: string): boolean {
  return !isAddressWord(token) && !NOT_A_STATE_WORDS.has(token.toLowerCase());
}

function tokensOf(segment: string): string[] {
  return segment.split(/\s+/).filter((t) => t !== '');
}

function hasLetter(text: string): boolean {
  return /[A-Za-z]/.test(text);
}

function isSuffixToken(token: string): boolean {
  return SUFFIX_WORDS.has(token.replace(/\./g, '').toLowerCase());
}

/** One to Ten, written with a capital first letter ("One", "ONE"). */
function isSpelledHouseNumber(token: string): boolean {
  const t = token.replace(/\./g, '');
  return /^[A-Z]/.test(t) && SPELLED_HOUSE_NUMBERS.has(t.toLowerCase());
}

/** True when some run of the tokens is a route. A state code opens one
 *  only when it is written in capitals, so prose ("in 2 places") is not. */
function hasRoute(tokens: readonly string[]): boolean {
  for (let start = 0; start < tokens.length; start += 1) {
    const lead = /^[A-Za-z]+/.exec(tokens[start]!)?.[0] ?? '';
    const opensAsWord = ROUTE_OPENING_WORDS.has(lead.toLowerCase());
    if (!opensAsWord && codeOf(lead) !== null && lead !== lead.toUpperCase()) continue;
    for (let end = start + 1; end <= tokens.length; end += 1) {
      if (canonicalRouteName(tokens.slice(start, end).join(' ')) !== null) return true;
    }
  }
  return false;
}

/** A segment that opens with a digit house number and has a word after it. */
function segmentOpensWithNumber(tokens: readonly string[]): boolean {
  return tokens.length > 0 && HOUSE_NUMBER_TOKEN.test(tokens[0]!) && tokens.slice(1).some(hasLetter);
}

/** A house number after a leading name (digits past the first token) or
 *  spelled out (anywhere), with a street suffix two or more words on. */
function hasNumberThenSuffix(tokens: readonly string[]): boolean {
  for (let i = 0; i < tokens.length; i += 1) {
    const t = tokens[i]!;
    const isNumber = (i >= 1 && HOUSE_NUMBER_TOKEN.test(t)) || isSpelledHouseNumber(t);
    if (isNumber && tokens.slice(i + 2).some(isSuffixToken)) return true;
  }
  return false;
}

/** One segment's street shape: a house number then a word, a route, a
 *  street suffix ending two or more tokens, or a house number with a
 *  street suffix two or more words on. */
function segmentHasStreetShape(segment: string): boolean {
  const tokens = tokensOf(segment);
  if (tokens.length === 0) return false;
  if (segmentOpensWithNumber(tokens)) return true;
  if (hasRoute(tokens)) return true;
  if (tokens.length >= 2 && isSuffixToken(tokens[tokens.length - 1]!)) return true;
  return hasNumberThenSuffix(tokens);
}

/** The segment without a country that follows its ZIP ("TX 78751 USA"). */
function withoutCountryAfterZip(segment: string): string {
  const tokens = tokensOf(segment);
  for (let i = tokens.length - 1; i >= 1; i -= 1) {
    if (ZIP_TOKEN.test(tokens[i - 1]!) && COUNTRY_KEYS.has(countryKey(tokens.slice(i).join('')))) {
      return tokens.slice(0, i).join(' ');
    }
  }
  return segment;
}

const NOTHING_READ: FreeTextAddress = {
  zip: null,
  state: null,
  zipOnly: false,
  streetShape: false,
  streetOpensWithNumber: false,
  townShape: false,
  judged: [],
};

/** The text's comma segments, trimmed and non-empty, a country dropped
 *  (a final country segment, or one following the ZIP at the end of the
 *  last segment) and a final segment that is only a ZIP folded into the
 *  segment before it. The reader and the split both segment here. */
function segmentFreeText(text: string): string[] {
  const segments = text
    .trim()
    .split(',')
    .map((s) => s.trim())
    .filter((s) => s !== '');
  if (segments.length > 0 && COUNTRY_KEYS.has(countryKey(segments[segments.length - 1]!))) segments.pop();
  if (segments.length === 0) return [];
  segments[segments.length - 1] = withoutCountryAfterZip(segments[segments.length - 1]!);
  // A final segment that is only a ZIP is read as the last token of the
  // segment before it.
  if (segments.length >= 2 && ZIP_TOKEN.test(segments[segments.length - 1]!)) {
    const zipSegment = segments.pop()!;
    segments[segments.length - 1] = `${segments[segments.length - 1]!} ${zipSegment}`;
  }
  return segments;
}

/** What the reader took from the segments, beside what it reports: the
 *  whole ZIP token (ZIP+4 kept) and the state's words as typed. */
interface ReadSegments extends FreeTextAddress {
  zipToken: string | null;
  stateWords: string[];
}

/** Reads the ZIP, the state, whether the text is a ZIP alone, its street
 *  shape and its town shape from the segments. */
function readSegments(segments: string[]): ReadSegments {
  if (segments.length === 0) return { ...NOTHING_READ, judged: [], zipToken: null, stateWords: [] };

  const lastIndex = segments.length - 1;
  const finalTokens = tokensOf(segments[lastIndex]!);
  const lastToken = finalTokens[finalTokens.length - 1] ?? '';
  const zipToken = ZIP_TOKEN.test(lastToken) ? lastToken : null;
  const zip = zipToken === null ? null : zipToken.slice(0, 5);
  const zipOnly = zipToken !== null && segments.length === 1 && segments[0] === zipToken;

  // The final segment without its ZIP, dots removed.
  const withoutZip = zipToken === null ? finalTokens : finalTokens.slice(0, -1);
  const T = withoutZip.map((t) => t.replace(/\./g, '')).filter((t) => t !== '');

  let state: string | null = null;
  /** Which tokens of the final segment the state took: a trailing token,
   *  or the whole segment for a name. */
  let stateTakes: 'last' | 'all' | null = null;
  if (zipOnly) {
    // A ZIP alone carries no state.
  } else if (segments.length === 1) {
    const tail = T[T.length - 1];
    if (tail !== undefined) {
      const code = codeOf(tail);
      // With no ZIP, a code is read only in capitals after a word.
      const readable = zipToken !== null || (tail === tail.toUpperCase() && T.slice(0, -1).some(hasLetter));
      if (code !== null && readable && passesCodeGuard(tail)) {
        state = code;
        stateTakes = 'last';
      }
    }
  } else if (T.length === 1) {
    const code = codeOf(T[0]!);
    if (code !== null) {
      state = code;
      stateTakes = 'last';
    }
  } else if (T.length >= 2) {
    const tail = T[T.length - 1]!;
    const code = codeOf(tail);
    if (code !== null && passesCodeGuard(tail)) {
      state = code;
      stateTakes = 'last';
    }
  }
  if (state === null && !zipOnly && T.length >= 1 && segments.length >= 2) {
    const names = segments.length >= 3 ? CODE_BY_NAME : CODE_BY_SPACED_NAME;
    const byName = names.get(normalName(T.join(' ')));
    if (byName !== undefined) {
      state = byName;
      stateTakes = 'all';
    }
  }

  // The street shape, judged with the ZIP and the state taken out.
  let finalRemaining = withoutZip;
  let stateWords: string[] = [];
  if (stateTakes === 'last') {
    finalRemaining = withoutZip.slice(0, -1);
    stateWords = withoutZip.slice(-1);
  }
  if (stateTakes === 'all') {
    finalRemaining = [];
    stateWords = withoutZip;
  }
  const judged = [...segments.slice(0, lastIndex), finalRemaining.join(' ')].filter((s) => s.trim() !== '');
  const streetShape = !zipOnly && judged.some(segmentHasStreetShape);
  const streetOpensWithNumber = !zipOnly && judged.some((s) => segmentOpensWithNumber(tokensOf(s)));

  const townPosition = judged[judged.length - 1];
  const positionWords = townPosition === undefined ? [] : tokensOf(townPosition);
  const lastWord = (positionWords[positionWords.length - 1] ?? '').replace(/\./g, '').toLowerCase();
  const townShape =
    !zipOnly && !streetShape && state !== null && judged.some(hasLetter) && !COUNTY_WORDS.has(lastWord);

  return { zip, state, zipOnly, streetShape, streetOpensWithNumber, townShape, judged, zipToken, stateWords };
}

/** Reads the ZIP, the state, whether the text is a ZIP alone, its street
 *  shape and its town shape. Pure; never sends anything. */
export function readFreeTextAddress(text: string): FreeTextAddress {
  const { zip, state, zipOnly, streetShape, streetOpensWithNumber, townShape, judged } = readSegments(segmentFreeText(text));
  return { zip, state, zipOnly, streetShape, streetOpensWithNumber, townShape, judged };
}

function blank(v: string | null | undefined): boolean {
  return (v ?? '').trim() === '';
}

/** True when a structured address asks at town level: its line 1 is a PO
 *  Box line, or it has a town and no street. Only the town, state and ZIP
 *  of such an ask are sent. */
export function isStructuredTownAsk(address: GeocodeAddress): boolean {
  if (isPoBoxLine(address.line1 ?? '')) return true;
  return blank(address.line1) && !blank(address.city);
}

/** What an ask typed, read once for the send test and the verdict. */
interface Typed {
  state: string | null;
  zip: string | null;
  zipOnly: boolean;
  freeText: boolean;
  /** Free text with a street shape. */
  street: boolean;
  streetOpensWithNumber: boolean;
  /** A town and a state: free text sendable only as one, or a structured
   *  town ask. */
  town: boolean;
  /** The town position: the last judged segment of free text, the typed
   *  town of a structured town ask; null otherwise. */
  townPosition: string | null;
}

function typedOf(ask: GeocodeAsk): Typed {
  if (ask.kind === 'structured') {
    const townAsk = isStructuredTownAsk(ask.address);
    const zip = (ask.address.zip ?? '').trim();
    // A structured street ask has no shape test and never compares the
    // ZIP; a structured town ask carries its town and its ZIP's first five
    // for the anchor.
    return {
      state: typedStateCode(ask.address.state),
      zip: townAsk && /^\d{5}/.test(zip) ? zip.slice(0, 5) : null,
      zipOnly: false,
      freeText: false,
      street: false,
      streetOpensWithNumber: false,
      town: townAsk,
      townPosition: townAsk ? (ask.address.city ?? '').trim() : null,
    };
  }
  const read = readFreeTextAddress(ask.text);
  return {
    state: read.state,
    zip: read.zip,
    zipOnly: read.zipOnly,
    freeText: true,
    street: read.streetShape,
    streetOpensWithNumber: read.streetOpensWithNumber,
    town: read.townShape,
    townPosition: read.judged[read.judged.length - 1] ?? null,
  };
}

/** Sendable by its own shape: a structured ask, or free text with a
 *  street shape or a ZIP alone. */
function sendableByShape(typed: Typed): boolean {
  return !typed.freeText || typed.street || typed.zipOnly;
}

/** Sendable: by its shape, or as a town and a state, whatever the working
 *  states (a town and a state always reads a state). */
function sendable(typed: Typed): boolean {
  return sendableByShape(typed) || typed.town;
}

/** True when an ask may be sent: a structured ask always; free text with
 *  a street shape, a ZIP alone, or a town and a state. The working states
 *  decide where an untyped answer may be placed, never whether text is
 *  sent. */
export function mayBeSent(ask: GeocodeAsk, _rule: GeocodeRule): boolean {
  return sendable(typedOf(ask));
}

/** A town name as words: lower case, dots removed, each word through
 *  TOWN_TOKENS; its municipal suffix split off when another word
 *  precedes it. */
function townName(text: string): { core: string[]; suffix: string | null } {
  const words = tokensOf(text.toLowerCase().replace(/\./g, '')).map((w) => TOWN_TOKENS.get(w) ?? w);
  const last = words[words.length - 1];
  if (words.length >= 2 && last !== undefined && MUNICIPAL_SUFFIXES.has(last)) return { core: words.slice(0, -1), suffix: last };
  return { core: words, suffix: null };
}

/** True when the answer's town name ends the text's town position, word
 *  for word; a suffix on one side only is ignored, two must be the same.
 *  The split script confirms a PO Box or town-only split with it. */
export function endsTownPosition(position: string, name: string): boolean {
  const p = townName(position);
  const n = townName(name);
  if (n.core.length === 0 || n.core.length > p.core.length) return false;
  const offset = p.core.length - n.core.length;
  if (!n.core.every((w, i) => p.core[offset + i] === w)) return false;
  return p.suffix === null || n.suffix === null || p.suffix === n.suffix;
}

/** An answer a town and a state may be placed on: a town-level Geography
 *  (a Municipality or PostalCodeArea) or a street-level result. */
function takesTownText(match: GeocodeMatch): boolean {
  if (match.type === TOWN_RESULT_TYPE) return match.entityType !== null && TOWN_ENTITY_TYPES.has(match.entityType);
  return match.type !== null && STREET_RESULT_TYPES.has(match.type);
}

/** True when a town position names no town: no letters are left once box
 *  and unit words and every token holding a digit are set aside. */
function namesNoTown(position: string | null): boolean {
  if (position === null) return true;
  return !tokensOf(position.replace(/\./g, '')).some(
    (t) => !/\d/.test(t) && !BOX_AND_UNIT_WORDS.has(t.toLowerCase()) && hasLetter(t),
  );
}

/** A town-level or street-level answer anchored in what was typed: one
 *  of the town names it lists ends the town position; or it carries the
 *  typed ZIP and either names no town or the town position names none. A
 *  carried ZIP never overrides a town position that names a different
 *  town, so a list none of whose names ends the position refuses it. */
function townAnswerAnchored(typed: Typed, match: GeocodeMatch): boolean {
  if (!takesTownText(match)) return false;
  const names = listedTownNames(match);
  const position = typed.townPosition;
  if (position !== null && names.some((name) => endsTownPosition(position, name))) return true;
  const carriesTypedZip = typed.zip !== null && match.zips.includes(typed.zip);
  return carriesTypedZip && (names.length === 0 || namesNoTown(position));
}

function refused(rejected: GeocodeRejection): PlacementVerdict {
  return { rejected, approximate: false };
}

/**
 * The placement verdict for an answer, in order: not sendable is no
 * street address; no answer is not found; the state (the typed one, else
 * nothing for a ZIP alone, else a working state; an answer with no state
 * code is not found where a state is tested); a town and a state (free
 * text, or a structured town ask) is placed, approximately, only on an
 * anchored town-level or street-level answer, else not found; for other
 * free text, a typed ZIP the answer does not carry marks it approximate
 * when a state was read and refuses it when none was (a ZIP alone must
 * be carried, and one whose answer carries no postal code is not found);
 * a free-text street whose segment does not open with a digit house
 * number is approximate unless the answer carries the typed ZIP.
 * A structured street ask is never marked approximate.
 */
export function acceptGeocodeMatch(ask: GeocodeAsk, match: GeocodeMatch | null, rule: GeocodeRule): PlacementVerdict {
  const typed = typedOf(ask);
  if (!sendable(typed)) return refused('no_street_address');
  if (match === null) return refused('not_found');

  if (typed.state !== null) {
    if (match.stateCode === null) return refused('not_found');
    if (match.stateCode !== typed.state) return refused('state_mismatch');
  } else if (!typed.zipOnly) {
    if (match.stateCode === null) return refused('not_found');
    if (!rule.workingStates.includes(match.stateCode)) return refused('outside_working_states');
  }

  if (typed.town) {
    // A town and a state.
    if (!townAnswerAnchored(typed, match)) return refused('not_found');
    return { rejected: null, approximate: true };
  }
  if (!typed.freeText) return { rejected: null, approximate: false };

  const carriesTypedZip = typed.zip !== null && match.zips.includes(typed.zip);
  let approximate = false;
  if (typed.zip !== null) {
    if (match.zips.length > 0) {
      if (!carriesTypedZip) {
        if (typed.zipOnly || typed.state === null) return refused('zip_mismatch');
        approximate = true;
      }
    } else if (typed.zipOnly) {
      return refused('not_found');
    }
  }
  if (!typed.zipOnly && !typed.streetOpensWithNumber && !carriesTypedZip) approximate = true;
  return { rejected: null, approximate };
}

/**
 * Why a row that was geocoded holds no point, from what was typed alone:
 * free text with no street shape that is not a ZIP alone is no street
 * address (a town and a state included); no typed state (and not a ZIP
 * alone) asks for the state, whether or not anything was found; anything
 * else, a structured town ask's miss included, is plain not found.
 */
export function notPlacedReasonFor(ask: GeocodeAsk): NotPlacedReason {
  const typed = typedOf(ask);
  if (!sendableByShape(typed)) return 'no_street_address';
  if (typed.state === null && !typed.zipOnly) return 'not_found_add_state';
  return 'not_found';
}

/** The placement rule from the tenant's working states (listWorkingStates),
 *  their codes in the order given. */
export function placementRuleOf(workingStates: ReadonlyArray<{ code: string }>): GeocodeRule {
  return { workingStates: workingStates.map((s) => s.code) };
}

// ---------------------------------------------------------------------
// The split of stored text into its parts
// ---------------------------------------------------------------------

/** Why a text is kept as it is, the first rule it fails. */
export type AddressSplitReason =
  | 'blank'
  | 'more_than_one_address'
  | 'no_state'
  | 'one_part_only'
  | 'too_many_parts'
  | 'no_house_number'
  | 'second_line_not_a_unit'
  | 'town_not_a_town'
  | 'over_cap'
  | 'words_left_over'
  | 'no_zip';

/** Every reason, in the order the split's report prints them. */
export const ADDRESS_SPLIT_REASONS: readonly AddressSplitReason[] = [
  'blank',
  'more_than_one_address',
  'no_state',
  'one_part_only',
  'too_many_parts',
  'no_house_number',
  'second_line_not_a_unit',
  'town_not_a_town',
  'over_cap',
  'words_left_over',
  'no_zip',
];

/** The five parts a split yields: line 1, line 2 and the town as typed
 *  (whitespace collapsed), the state as its code, the ZIP through
 *  normalizeZip (ZIP+4 kept) or null. */
export interface AddressSplitParts {
  line1: string | null;
  line2: string | null;
  city: string;
  state: string;
  zip: string | null;
}

export type AddressSplit = { kind: 'split'; parts: AddressSplitParts } | { kind: 'kept'; reason: AddressSplitReason };

/** Which table's text is split: the Directory ('directory': a town and a
 *  state may stand alone), a staff home ('home': a ZIP is required) or a
 *  billing contact ('street'). */
export type AddressSplitMode = 'directory' | 'home' | 'street';

/** The caps the address form applies: line 1 and line 2, and the town. */
const SPLIT_MAX_LINE = 200;
const SPLIT_MAX_CITY = 100;

/** A word a town never holds, beside the unit and floor keywords. */
const NOT_TOWN_WORDS: ReadonlySet<string> = new Set(['building', 'level', 'rear']);

/** Rule 1: line breaks and ";" read as commas, a token of only dots and
 *  commas dropped, whitespace collapsed, the text trimmed. */
function normaliseForSplit(text: string): string {
  return text
    .replace(/[;\r\n]+/g, ',')
    .split(/\s+/)
    .filter((t) => t !== '' && !/^[.,]+$/.test(t))
    .join(' ')
    .trim();
}

/** A segment that is, dots removed, exactly a state code or a state or
 *  territory name. */
function isStateSegment(segment: string): boolean {
  const T = tokensOf(segment).map((t) => t.replace(/\./g, '')).filter((t) => t !== '');
  if (T.length === 1 && codeOf(T[0]!) !== null) return true;
  return CODE_BY_NAME.has(normalName(T.join(' ')));
}

/** The index of the first " and ", " & " or "/" in a segment, and its
 *  length; null when none. */
function firstSeparator(segment: string): { at: number; length: number } | null {
  const lower = segment.toLowerCase();
  const found = [
    { at: lower.indexOf(' and '), length: 5 },
    { at: lower.indexOf(' & '), length: 3 },
    { at: lower.indexOf('/'), length: 1 },
  ].filter((s) => s.at >= 0);
  if (found.length === 0) return null;
  return found.reduce((a, b) => (b.at < a.at ? b : a));
}

/** Rule 2: the text holds two addresses when a segment other than the
 *  last holds a ZIP token; a segment other than the last is a state code
 *  or name and the next opens with a digit house number or is a PO Box
 *  line; or a segment splits at its first " and ", " & " or "/" into a
 *  left side holding a word with a letter and a right side that opens
 *  with a digit house number followed by a word with a letter. */
function holdsTwoAddresses(segments: readonly string[]): boolean {
  for (let i = 0; i < segments.length - 1; i += 1) {
    const segment = segments[i]!;
    if (tokensOf(segment).some((t) => ZIP_TOKEN.test(t))) return true;
    const next = segments[i + 1]!;
    if (isStateSegment(segment) && (segmentOpensWithNumber(tokensOf(next)) || isPoBoxLine(next))) return true;
  }
  return segments.some((segment) => {
    const sep = firstSeparator(segment);
    if (sep === null) return false;
    const left = tokensOf(segment.slice(0, sep.at));
    const right = tokensOf(segment.slice(sep.at + sep.length));
    return left.some(hasLetter) && segmentOpensWithNumber(right);
  });
}

/** Rule 6: a street line opens with a digit house number and has a word
 *  with a letter after it, or is a PO Box line with nothing after its
 *  number. */
function isStreetLine(line: string): boolean {
  if (segmentOpensWithNumber(tokensOf(line))) return true;
  const tail = poBoxTail(line);
  return tail !== null && tail.length === 1 && /\d/.test(tail[0]!);
}

/** Rule 8: a town holds a letter and no digit, its last word is no county
 *  word, and no word of it is a unit, floor, building, level, rear or
 *  ordinal word. */
function isTownEntry(entry: string): boolean {
  const words = tokensOf(entry);
  if (words.length === 0 || !hasLetter(entry) || /\d/.test(entry)) return false;
  const keyOf = (w: string): string => w.replace(/\./g, '').toLowerCase();
  if (COUNTY_WORDS.has(keyOf(words[words.length - 1]!))) return false;
  return !words.some((w) => {
    const key = keyOf(w);
    return UNIT_KEYWORDS.has(key) || FLOOR_KEYWORDS.has(key) || NOT_TOWN_WORDS.has(key) || isOrdinal(w);
  });
}

/** A unit's number or letter after its word: holds a digit, or is one
 *  letter. */
function isUnitNumber(token: string): boolean {
  return /\d/.test(token) || /^[A-Za-z]$/.test(token);
}

/** Rule 5a: an entry holding a unit and then the town, with no comma
 *  between: the words before the unit, the unit, and the town's words;
 *  null when the entry holds no unit followed by a word. */
function unitThenTown(entry: string): { before: string; unit: string; town: string } | null {
  const words = tokensOf(entry);
  for (let i = 0; i < words.length; i += 1) {
    const word = words[i]!;
    const key = word.replace(/\.$/, '').toLowerCase();
    let unitLength = 0;
    if (UNIT_KEYWORDS.has(key) && words[i + 1] !== undefined && isUnitNumber(words[i + 1]!)) unitLength = 2;
    else if (/^#\S*\d/.test(word)) unitLength = 1;
    if (unitLength === 0 || i + unitLength >= words.length) continue;
    return {
      before: words.slice(0, i).join(' '),
      unit: words.slice(i, i + unitLength).join(' '),
      town: words.slice(i + unitLength).join(' '),
    };
  }
  return null;
}

/** Rule 5a applied to the entries: three when the one entry holds the
 *  street, the unit and the town, else the entries as they are. */
function withUnitTownSplit(entries: string[]): string[] {
  if (entries.length !== 1) return entries;
  const read = unitThenTown(entries[0]!);
  if (read === null || read.before === '' || !isStreetLine(read.before)) return entries;
  return [read.before, read.unit, read.town];
}

function collapse(text: string): string {
  return tokensOf(text).join(' ');
}

function kept(reason: AddressSplitReason): AddressSplit {
  return { kind: 'kept', reason };
}

/**
 * The five parts of a stored free-text address, when every rule holds
 * (the header lists them in order), else the first rule it fails. Pure;
 * the split script confirms a split with the geocoder before it writes.
 */
export function splitFreeTextAddress(text: string, mode: AddressSplitMode): AddressSplit {
  const normalised = normaliseForSplit(text);
  if (normalised === '') return kept('blank');
  const segments = segmentFreeText(normalised);
  if (segments.length === 0) return kept('blank');
  if (holdsTwoAddresses(segments)) return kept('more_than_one_address');
  const read = readSegments(segments);
  if (read.state === null || read.zipOnly) return kept('no_state');

  const entries = withUnitTownSplit(read.judged.map(collapse));
  let line1: string | null = null;
  let line2: string | null = null;
  let city: string;
  if (entries.length <= 1) {
    if (mode !== 'directory') return kept('one_part_only');
    city = entries[0] ?? '';
  } else if (entries.length > 3) {
    return kept('too_many_parts');
  } else {
    line1 = entries[0]!;
    if (!isStreetLine(line1)) return kept('no_house_number');
    if (entries.length === 3) {
      line2 = entries[1]!;
      if (!isUnitSegment(line2)) return kept('second_line_not_a_unit');
    }
    city = entries[entries.length - 1]!;
  }
  if (!isTownEntry(city)) return kept('town_not_a_town');
  if ((line1?.length ?? 0) > SPLIT_MAX_LINE || (line2?.length ?? 0) > SPLIT_MAX_LINE || city.length > SPLIT_MAX_CITY) {
    return kept('over_cap');
  }

  const split: AddressSplit = {
    kind: 'split',
    parts: { line1, line2, city, state: read.state, zip: read.zipToken === null ? null : normalizeZip(read.zipToken) },
  };
  if (!splitHoldsEveryWord(text, split)) return kept('words_left_over');
  if (mode === 'home' && split.parts.zip === null) return kept('no_zip');
  return split;
}

/** The state's words as typed read as the split's code: one token as a
 *  code (dots removed), or the words as a state or territory name. */
function readsAsState(words: readonly string[], code: string): boolean {
  const T = words.map((t) => t.replace(/\./g, '')).filter((t) => t !== '');
  if (T.length === 0) return false;
  if (T.length === 1 && codeOf(T[0]!) === code) return true;
  return CODE_BY_NAME.get(normalName(T.join(' '))) === code;
}

/**
 * Rule 10: true when the normalised text's words, a country and every
 * comma dropped, are line 1, line 2, the town, the state's words as typed
 * and the ZIP as typed, in that order, and nothing else.
 */
export function splitHoldsEveryWord(text: string, split: Extract<AddressSplit, { kind: 'split' }>): boolean {
  const words = segmentFreeText(normaliseForSplit(text)).flatMap(tokensOf);
  const { parts } = split;
  const expected = [...tokensOf(parts.line1 ?? ''), ...tokensOf(parts.line2 ?? ''), ...tokensOf(parts.city)];
  if (words.length < expected.length || !expected.every((w, i) => words[i] === w)) return false;
  const rest = words.slice(expected.length);
  const last = rest[rest.length - 1];
  if (parts.zip !== null) {
    if (last === undefined || !ZIP_TOKEN.test(last) || normalizeZip(last) !== parts.zip) return false;
    rest.pop();
  }
  return readsAsState(rest, parts.state);
}
